import React from 'react';
import {
    VStack,
    HStack,
    Text,
    Icon,
    IconButton,
    Tooltip,
    Avatar,
    Box,
} from '@chakra-ui/react';
import { GoPlus } from 'react-icons/go';
import { HiDotsHorizontal } from 'react-icons/hi';
import { ThreadListViewProps } from './threadListProps';
import { ThreadItemContainerProps, ThreadItemViewProps } from '../item';
import type { ResearchShellConfig } from '../../../researchShellConfig';
import { NavLink } from 'react-router-dom';
import { createMenuView } from '../../../../route/document/view/common/menu';

export function createThreadListView(
    config: ResearchShellConfig & {
        Container: {
            Thread: {
                Item: React.FC<ThreadItemContainerProps>;
            };
        };
    }
): React.FC<ThreadListViewProps> {
    const {
        Container: {
            Thread: { Item: ThreadItemContainer },
        },
    } = config;

    const MenuView = createMenuView();

    const ThreadItem: React.FC<ThreadItemViewProps> = (props) => {
        return (
            <HStack
                {...props.hover.element}
                as={NavLink}
                to={props.action.link.to}
                w="full"
                justify="space-between"
                p={2}
                cursor="pointer"
                fontWeight="medium"
                userSelect="none"
                borderRadius="md"
                color="whiteAlpha.600"
                _hover={{ bg: 'whiteAlpha.50', textDecoration: 'none' }}
                _focus={{ bg: 'whiteAlpha.50', textDecoration: 'none' }}
                _activeLink={{
                    bg: 'whiteAlpha.100',
                }}
                // avoid brief flickering after click before _activeLink state comes into play
                transition="background-color 0.1s ease"
                role="group"
            >
                <HStack spacing={2} flex={1} minW={0}>
                    <Box position="relative">
                        <Avatar
                            size="xs"
                            name={props.label}
                            h={6}
                            w={6}
                            minW={0}
                            minH={0}
                            fontWeight="bold"
                            borderWidth={2}
                            borderColor="blackAlpha.900"
                            borderRadius="md"
                        />
                        <Box
                            position="absolute"
                            bottom={-0.25}
                            right={-0.5}
                            w={2.5}
                            h={2.5}
                            borderRadius="full"
                            bg="green.400"
                            borderWidth={2}
                            borderColor="black"
                        />
                    </Box>
                    <Text noOfLines={1} fontWeight="medium" color="whiteAlpha.600">
                        {props.label}
                    </Text>
                </HStack>

                {/* Menu Button - following sidebar pattern with common MenuView */}
                {props.menu.isVisible && (
                    <MenuView
                        placement="bottom-end"
                        variant="compact"
                        minW="16rem"
                        popover={props.menu.popover}
                        items={props.menu.items}
                    >
                        <IconButton
                            color="whiteAlpha.500"
                            size="sm"
                            h={6}
                            w={6}
                            minW={0}
                            minH={0}
                            variant="outline"
                            bg="none"
                            icon={<Icon fontSize="md" as={HiDotsHorizontal} />}
                            _hover={{ bg: 'whiteAlpha.50' }}
                            _expanded={{ bg: 'whiteAlpha.100' }}
                            aria-label="More options"
                        />
                    </MenuView>
                )}
            </HStack>
        );
    };

    return (props) => {
        if (props.items.length === 0) {
            return null;
        }

        return (
            <VStack w="full" align="start" spacing={0.5} flexShrink={0}>
                {/* Direct Messages Header - styled like folder headers */}
                <HStack w="full" justify="space-between" p={2} pl={3} py={1}>
                    <Text
                        color="whiteAlpha.500"
                        fontSize="xs"
                        textTransform="uppercase"
                        fontWeight="bold"
                        letterSpacing="wide"
                    >
                        Direct Messages
                    </Text>
                    <Tooltip
                        placement="bottom"
                        bg="gray.50"
                        color="gray.900"
                        py={1}
                        px={2}
                        borderRadius="md"
                        openDelay={500}
                        label="Start new conversation"
                    >
                        <IconButton
                            flexShrink={0}
                            variant="outline"
                            size="sm"
                            h={6}
                            w={6}
                            minW={0}
                            minH={0}
                            icon={
                                <Icon as={GoPlus} fontSize="xl" color="whiteAlpha.500" />
                            }
                            aria-label="Start new conversation"
                            bg="none"
                            _hover={{ bg: 'whiteAlpha.100' }}
                            // onClick={() => props.actions?.onCreateThread?.()}
                        />
                    </Tooltip>
                </HStack>

                {/* Thread Items */}
                {props.items.map((item) => (
                    <ThreadItemContainer as={ThreadItem} key={item.id} item={item.item} />
                ))}
            </VStack>
        );
    };
}
